import { apiFetch } from "./client";

/**
 * A-Block A0 — legacy account-route resolver client.
 *
 * The holding-first SPA has no root-level legal-entity route; this resolves an
 * old account id to its active-holding anchor so legacy bookmarks
 * (/account/:id · /accounts/:id · #Account/view/:id) land correctly.
 *
 * Backend always answers 200 with this envelope; `resolved` distinguishes the
 * redirect target from the no-access case (AC-A0-05 — never a random holding).
 */
export interface LegacyAccountAnchor {
  resolved: boolean;
  accountId?: string;
  holdingId?: string;
  /** Pre-built SPA path, e.g. "/clients/{holdingId}/legal-entities/{accountId}". */
  path?: string;
  /** When resolved=false: missing-id | not-found | forbidden | no-active-membership | lookup-error. */
  reason?: string;
}

export function fetchLegacyAccountAnchor(
  accountId: string,
  signal?: AbortSignal,
): Promise<LegacyAccountAnchor> {
  return apiFetch<LegacyAccountAnchor>(
    `/Custom/frontend/legacy/account-anchor/${encodeURIComponent(accountId)}`,
    { signal },
  );
}

// ── A-Block A1 — ClientsList ────────────────────────────────────────────────

export type ClientType = "holding" | "standalone";
export type AbcClass = "A" | "B" | "C";
export type RiskStatus = "red" | "yellow" | "green";
export type ClientScope = "my" | "team" | "all";
export type ClientView = "active" | "churned" | "no_orders" | "all";
export type PartyType = "organization" | "physical_person" | "unknown";

export interface ClientMember {
  id: string;
  name: string;
  code: string;
  edrpou: string;
  ipn: string;
  ar: number;
  lastOrderAt: string | null;
  active: boolean;
  partyType: PartyType;
}

/** Этап 7 — next-action surfaced on the row (3A source). */
export interface ClientNextAction {
  label: string;
  date: string | null;
  overdue: boolean;
}

export type ClientDqFlag =
  | "overdue" | "no_contact" | "no_profile" | "unclassified"
  | "profile_unverified" | "no_next_action" | "high_risk";

export interface ClientRow {
  id: string;
  type: ClientType;
  name: string;
  /** Этап 7 — record-type axis (CClientHolding.cCRecordType). */
  recordType: string;
  isService: boolean;
  businessModel: string | null;
  manager: string;
  region: string;
  segment: string;
  memberCount: number;
  abc: AbcClass | null;
  risk: RiskStatus | null;
  revenue: number | null;
  revenue30: number | null;
  ar: number | null;
  overdue: number | null;
  lastOrderAt: string | null;
  /** ≠ lastOrderAt — last contact (3C/timeline). */
  lastContactAt: string | null;
  nextAction: ClientNextAction | null;
  dqFlags: ClientDqFlag[];
  attentionCount: number;
  /** D-140 — archived client (holding-grain: ALL active members archived). */
  archived?: boolean;
  members: ClientMember[];
}

/** D-140 W6 — tri-state archived filter (backend-owned, default exclude). */
export type ArchivedFilter = "exclude" | "include" | "only";

/** Этап 7 — backend-owned quick-segment counts (§3). */
export interface ClientQuickSegments {
  mine: number;
  team: number;
  overdueDebt: number;
  noOrdersThisMonth: number;
  attention: number;
  unclassified: number;
  noPrimaryContact: number;
  missingProfile: number;
  profileOnReview: number;
  highRisk: number;
  noNextAction: number;
  breeders: number;
  holdings: number;
  technical: number;
}

export type ClientSourceStatus = "available" | "unavailable" | "stale" | "forbidden";

export interface ClientListResponse {
  items: ClientRow[];
  nextCursor: string | null;
  totalVisibleCount: number;
  listBiAvailability: { abcRiskFiltersAvailable: boolean };
  allowedScopes: ClientScope[];
  managers: Array<{ id: string; name: string }>;
  facets: {
    abc: Partial<Record<AbcClass, number>>;
    risk: Partial<Record<RiskStatus, number>>;
    recordTypes?: Record<string, number>;
  };
  quickSegments?: Partial<ClientQuickSegments>;
  sourceStatus?: { nextAction: ClientSourceStatus; lastContact: ClientSourceStatus };
}

export interface ClientListQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  scope?: ClientScope;
  manager?: string;
  abc?: string;
  risk?: string;
  type?: string;
  view?: ClientView;
  // Этап 7 registry filters + quick-segment.
  recordType?: string;
  personType?: string;
  businessModel?: string;
  debtStatus?: string;
  orderStatus?: string;
  dataQuality?: string;
  profileStatus?: string;
  city?: string;
  segment?: string;
  /** D-140 W6 — exclude (default) | include | only. */
  archived?: ArchivedFilter;
}

export function fetchClientList(
  q: ClientListQuery = {},
  signal?: AbortSignal,
): Promise<ClientListResponse> {
  return apiFetch<ClientListResponse>("/Custom/frontend/clients", {
    query: {
      cursor: q.cursor || undefined,
      limit: q.limit,
      q: q.q || undefined,
      scope: q.scope,
      manager: q.manager || undefined,
      abc: q.abc || undefined,
      risk: q.risk || undefined,
      type: q.type || undefined,
      view: q.view,
      recordType: q.recordType || undefined,
      personType: q.personType || undefined,
      businessModel: q.businessModel || undefined,
      debtStatus: q.debtStatus || undefined,
      orderStatus: q.orderStatus || undefined,
      dataQuality: q.dataQuality || undefined,
      profileStatus: q.profileStatus || undefined,
      city: q.city || undefined,
      segment: q.segment || undefined,
      // D-140 W6 — only send when narrowing away from the default exclude.
      archived: q.archived && q.archived !== "exclude" ? q.archived : undefined,
    },
    signal,
  });
}

// ── A-Block A2 — Client360 compact envelope ─────────────────────────────────

export type ClientRecordType =
  | "real_b2b_client"
  | "breeder_person"
  | "technical_account"
  | "event_exhibition_account"
  | "internal_account"
  | "retail_b2c_customer"
  | "unknown";

/** Legal-identity axis (Account grain) — read-only in the classification drawer. */
export type LegalIdentityType = "organization" | "fop" | "individual" | "";

export interface Client360Header {
  id: string;
  type: ClientType;
  name: string;
  displayName: string;
  /** Client360 Overview 2.0 (Этап 1) — record-type axis (header pill). */
  recordType: ClientRecordType;
  /** Derive v1.2 — '' | auto_derived | manual | verified («авто — перевірте» hint). */
  recordTypeSource?: string;
  /** Holding-grain additional-activities axis (drawer prefill). */
  additionalActivities: string[];
  cCPartyType: PartyType;
  variant: PartyType;
  subvariant: "fop" | "company" | null;
  titleLabel: string;
  identifier: { label: string; value: string } | null;
  legalForm: string;
  /** Representative account legal-identity (read-only in the classification drawer). */
  legalIdentityType: LegalIdentityType;
  manager: string;
  region: string;
  activeMembers: number;
  classificationPrompt: string | null;
  badge: { label: string; icon: string };
  /** D-140 — archived client (holding-grain: ALL active members archived). */
  archived?: boolean;
}

export interface Client360BiStrip {
  holdingRevenue365d: number | null;
  holdingRevenue30d: number | null;
  holdingArTotal: number | null;
  holdingOverdueTotal: number | null;
  holdingAbc: AbcClass | null;
  holdingRiskStatus: RiskStatus | null;
  biSyncedAt: string | null;
}

export interface Client360BiAvailability {
  available: boolean;
  reason: string | null;
  snapshotAt: string | null;
  fields: Record<string, boolean>;
}

export interface Client360Counts {
  activeMembers: number;
  outletsCount: number;
  contactsCount: number;
  contractsCount: number;
  activitiesCount: number;
  complaintsActive: number;
  /** D-146 W2 — alive materials of the holding (badge = backend activeCount). */
  materialsCount: number;
}

export interface Client360AllowedActions {
  canViewLegalEntities: boolean;
  canViewContacts: boolean;
  canViewContracts: boolean;
  canCreateContract: boolean;
  canViewComplaints: boolean;
  canCreateComplaint: boolean;
  canCreateActivity: boolean;
  canCreateAction?: boolean;
  canEditClientNotes: boolean;
  canOpenBusinessProfile: boolean;
  // 3A Task Core — operational CTAs (backend-owned).
  canCreateTask?: boolean;
  canLogCall?: boolean;
  canCompleteTask?: boolean;
  canViewTimeline?: boolean;
  // 3C Meeting Surface — CTAs (backend-owned, Google-free). VISIT-GATE: backend
  // sets canScheduleVisit=false + visitDisabledReason until Этап 5 (frontend
  // NEVER infers visit availability).
  canScheduleMeeting?: boolean;
  canScheduleVisit?: boolean;
  canFillReport?: boolean;
  visitDisabledReason?: string;
}

export interface Client360TabAvailability {
  overview: boolean;
  entities: boolean;
  contacts: boolean;
  contracts: boolean;
  outlets?: boolean;
  activities: boolean;
  tasks?: boolean;
  complaints: boolean;
  profile: boolean;
  /** D-146 W2 — Матеріали (visible to anyone who sees the card). */
  materials?: boolean;
}

export interface Client360KeyContact {
  name: string;
  role: string;
  phone: string;
  primary: boolean;
}

export interface Client360OverviewSummary {
  activeMembers: number;
  outletsCount: number;
  contactsCount: number;
  contractsCount: number;
  keyContacts: Client360KeyContact[];
  clientIntelligence: string[];
}

export interface Client360BusinessProfileSummary {
  status: "reviewed" | "pending";
  branch: "commercial_non_breeder" | "breeder";
  completeness: { requiredFilled: number; requiredTotal: number; percent: number };
  businessModels: string[];
  primaryBusinessModel: string | null;
  missingItems: string[];
  recommendedMissing: string[];
  allowedActions: { canOpenBusinessProfile: boolean };
  exists?: boolean;
}

export interface Client360Envelope {
  /**
   * D-144 П-6 — set when the requested holding was merged into a canonical
   * survivor: the envelope carries ONLY this field and the page must navigate
   * (replace) to the canonical id. Server checks target ACL BEFORE revealing.
   */
  redirectTo?: string;
  header: Client360Header;
  biStrip: Client360BiStrip;
  biAvailability: Client360BiAvailability;
  counts: Client360Counts;
  allowedActions: Client360AllowedActions;
  tabAvailability: Client360TabAvailability;
  overviewSummary: Client360OverviewSummary;
  businessProfileSummary: Client360BusinessProfileSummary | null;
  /** BUNDLE 1 STEP 5 — credit roll-up A (primary active supply contract, E1). */
  creditFigure: Client360CreditFigure | null;
  meta: { updatedAt: string; partial: boolean; warnings: string[] };
}

export interface Client360CreditFigure {
  determined: boolean;
  label?: string;
  creditLimit?: number | null;
  currency?: string | null;
  contractId?: string;
  contractNumber?: string;
  source?: string;
}

export function fetchClient360(
  holdingId: string,
  signal?: AbortSignal,
): Promise<Client360Envelope> {
  return apiFetch<Client360Envelope>(
    `/Custom/frontend/clients/${encodeURIComponent(holdingId)}`,
    { signal },
  );
}

// ── A-Block A4 — manual client-type classification (F4B variant drawer) ──────

export type ClassifiablePartyType = "organization" | "physical_person";

/** business_model axis (CClientBusinessProfile.cCPrimaryBusinessModel — REUSE 3.0A-4). */
export type ClientBusinessModel =
  | "independent_pet_shop" | "retail_chain" | "vet_clinic" | "grooming_salon"
  | "breeder_professional" | "online_store" | "marketplace_seller" | "wholesale_reseller"
  | "dealer_partner" | "purchasing_group" | "shelter_ngo" | "trainer_kennel_club"
  | "farm_agro_pet_point" | "other";

/**
 * Classification Grain Reconcile Hotfix P0 (§4) — holding-classification ONLY.
 * Legal identity (partyType / legalForm / ЄДРПОУ / ІПН) is NEVER written here;
 * it lives on the legal-entity (Account) grain, edited on the LE surface.
 */
export interface ClassifyClientPayload {
  /** record-type axis (CClientHolding.cCRecordType). */
  recordType?: ClientRecordType;
  /** business-model axis (real clients) → linked CClientBusinessProfile. */
  businessModel?: ClientBusinessModel;
  /** additional-activities axis (CClientHolding.cCAdditionalActivities). */
  additionalActivities?: string[];
}

export interface ClassifyClientResponse {
  ok: boolean;
  holdingId: string;
  accountId: string | null;
  recordType: ClientRecordType;
  previousRecordType: ClientRecordType;
  businessModel: ClientBusinessModel | null;
  additionalActivities: string[];
  legalIdentityType: LegalIdentityType | null;
  displayName: string;
}

export function classifyClient(
  holdingId: string,
  payload: ClassifyClientPayload,
): Promise<ClassifyClientResponse> {
  return apiFetch<ClassifyClientResponse>(
    `/Custom/frontend/clients/${encodeURIComponent(holdingId)}/classify`,
    { method: "POST", body: payload },
  );
}

// ── Client360 Overview 2.0 (Этап 1) — dedicated lazy overview endpoint ───────
// GET /Custom/frontend/client360/:holdingId/overview · §1.3 contract.
// Backend-owned: missing / dataQuality / nextBestAction / allowedActions are
// computed server-side; the UI renders verbatim and draws a CTA ONLY if its
// action is present in allowedActions.

/** Action verbs the backend may grant (frontend maps each to a real target). */
export type OverviewAction =
  | "createActivity" | "createAction" | "classify" | "openProfile"
  | "viewContacts" | "addContact" | "viewLegalEntities" | "viewContracts"
  | "addOutlet";

export type AttentionSeverity = "red" | "amber" | "blue" | "green";

export interface AttentionItem {
  code: string;
  label: string;
  detail: string | null;
  severity: AttentionSeverity;
  ctaAction: OverviewAction | null;
  anchor: string | null;
}

export interface EnrichmentItem {
  source: string;
  suggestion: string;
  acceptAction: string | null;
  rejectAction: string | null;
}

export interface NextBestAction {
  label: string;
  reason: string;
  ctaAction: OverviewAction;
  anchor: string | null;
}

export interface OverviewKeyContact {
  id: string;
  name: string;
  position: string;
  role: string;
  roles: string[];
  phone: string;
  primary: boolean;
  visibleMethods: Array<{ type: string; value: string }>;
}

export interface OverviewOpenTask {
  id: string;
  title: string;
  due: string | null;
  overdue: boolean;
  severity: AttentionSeverity;
}

export interface OverviewSnapshots {
  legalEntities: Array<{ id: string; name: string; debt: number; contractStatus: string | null; source: string | null }>;
  outlets: Array<{ id: string; name: string; city: string }>;
  contracts: Array<{ id: string; num: string; type: string; status: string; source: string }>;
}

export interface OverviewActivityItem {
  id: string;
  date: string;
  type: "meeting" | "call" | "task" | string;
  summary: string;
  outcome: string;
  who: string;
  source: string;
}

/** empty ≠ unavailable: backend distinguishes real-empty from not-connected. */
export type BlockStatus = "ok" | "empty" | "unavailable" | "forbidden" | "hidden";

export interface Client360OverviewResponse {
  recordType: ClientRecordType;
  businessModel: ClientBusinessModel | null;
  additionalActivities: string[];
  classified: boolean;
  headerLine: {
    lastActivity: { date: string; type: string; outcome: string; label: string } | null;
    nextAction: { date: string; type: string; label: string } | null;
    overdueCount: number;
  };
  nextBestAction: NextBestAction | null;
  attention: {
    missing: AttentionItem[];
    dataQuality: AttentionItem[];
    enrichment: EnrichmentItem[];
  };
  keyContacts: OverviewKeyContact[];
  openTasks: OverviewOpenTask[];
  snapshots: OverviewSnapshots;
  recentActivity: OverviewActivityItem[];
  allowedActions: OverviewAction[];
  blockStatus: {
    attention: BlockStatus;
    recentActivity: BlockStatus;
    keyContacts: BlockStatus;
    openTasks: BlockStatus;
    snapshots: BlockStatus;
    enrichment: BlockStatus;
  };
  meta: { updatedAt: string };
}

export function fetchClient360Overview(
  holdingId: string,
  signal?: AbortSignal,
): Promise<Client360OverviewResponse> {
  return apiFetch<Client360OverviewResponse>(
    `/Custom/frontend/client360/${encodeURIComponent(holdingId)}/overview`,
    { signal },
  );
}

// ── Outlet quick-create (FIX-1) ──────────────────────────────────────────────
// POST /Custom/frontend/clients/:holdingId/outlet — minimal CRM-owned create for
// the «Торгові точки» snapshot. Gated by allowedActions.addOutlet (CClientOutlet
// create ACL). Full Точки tab + visits = Этап 5.

export type OutletType =
  | "retail-store" | "vet-clinic" | "grooming-salon"
  | "warehouse" | "office" | "e-commerce-pickup" | "delivery-only";

/** Этап 5 — address-picker payload (NP directory OR first-class manual fallback). */
export interface OutletAddressPayload {
  addressSource: "np" | "manual";
  npAreaRef?: string | null;
  npSettlementRef?: string | null;
  npStreetRef?: string | null;
  city?: string;
  streetText?: string;
  buildingText?: string;
}

export interface CreateOutletPayload extends OutletAddressPayload {
  name: string;
  outletType: OutletType;
  /** Legacy free-text address draft (still accepted; superseded by streetText). */
  address?: string;
  active?: boolean;
  /** Optional operating legal entity (Account, must be a holding member). */
  companyId?: string | null;
  /** Optional point contact. */
  primaryContactId?: string | null;
}

export interface CreateOutletResponse {
  ok: boolean;
  outlet: { id: string; name: string; city: string };
}

export function createOutlet(
  holdingId: string,
  payload: CreateOutletPayload,
): Promise<CreateOutletResponse> {
  return apiFetch<CreateOutletResponse>(
    `/Custom/frontend/clients/${encodeURIComponent(holdingId)}/outlet`,
    { method: "POST", body: payload },
  );
}

// ── Этап 5 — outlets list + edit ─────────────────────────────────────────────

export type AddressQualityStatus = "verified_np" | "mixed" | "manual_unverified" | "needs_review";

export interface OutletRow {
  id: string;
  name: string;
  type: OutletType | string;
  active: boolean;
  city: string | null;
  address: string | null;
  addressQualityStatus: AddressQualityStatus;
  contactName: string | null;
  companyId: string | null;
  companyName: string | null;
  lastVisitDate: string | null;
  lastVisitDays: number | null;
  official: boolean;
  dq: string[];
}

export interface OutletListResponse {
  items: OutletRow[];
  counts: { total: number; active: number };
  cities: string[];
  mapAvailable: boolean;
  mapDisabledReason: string;
  restricted?: boolean;
  allowedActions: {
    canCreateOutlet: boolean;
    canEditOutlet: boolean;
    canScheduleVisit: boolean;
    canCreateTask: boolean;
  };
}

export function fetchOutlets(holdingId: string, signal?: AbortSignal): Promise<OutletListResponse> {
  return apiFetch<OutletListResponse>(
    `/Custom/frontend/clients/${encodeURIComponent(holdingId)}/outlets`,
    { signal },
  );
}

export interface UpdateOutletPayload extends Partial<OutletAddressPayload> {
  name?: string;
  outletType?: OutletType;
  active?: boolean;
  companyId?: string | null;
  primaryContactId?: string | null;
  comments?: string;
}

export function updateOutlet(outletId: string, payload: UpdateOutletPayload): Promise<{ ok: boolean; id: string; official: boolean }> {
  return apiFetch(
    `/Custom/frontend/clients/outlets/${encodeURIComponent(outletId)}`,
    { method: "PATCH", body: payload },
  );
}

// ── Этап 5 — NP address directory (read-only picker) ─────────────────────────

export interface NpArea { ref: string; name: string }
export interface NpSettlement { ref: string; name: string; type: string | null; areaName: string | null }
export interface NpStreet { ref: string; name: string }

export function fetchNpAreas(signal?: AbortSignal): Promise<{ items: NpArea[] }> {
  return apiFetch("/Custom/frontend/np/areas", { signal });
}
export function fetchNpSettlements(areaRef: string, q: string, signal?: AbortSignal): Promise<{ items: NpSettlement[] }> {
  return apiFetch("/Custom/frontend/np/settlements", { query: { areaRef: areaRef || undefined, q: q || undefined }, signal });
}
export function fetchNpStreets(settlementRef: string, q: string, signal?: AbortSignal): Promise<{ items: NpStreet[] }> {
  return apiFetch("/Custom/frontend/np/streets", { query: { settlementRef, q: q || undefined }, signal });
}

// ── Lazy tabs ────────────────────────────────────────────────────────────────

export type ClientTabKey =
  | "legal-entities"
  | "contracts"
  | "activities"
  | "tasks"
  | "contacts"
  | "complaints";

export interface ClientTabResponse<T = Record<string, unknown>> {
  items: T[];
  restricted?: boolean;
  nextCursor?: string | null;
}

export interface ClientLegalEntity {
  id: string;
  name: string;
  reg: string;
  ar: number;
  active: boolean;
  canCreateContract: boolean;
  // Этап 4 — list markers.
  isFop?: boolean;
  /** BUG3 — legal-identity axis for the mini-card label (фізособа ≠ ФОП). */
  legalIdentityType?: string;
  isPrimary?: boolean;
  hasActiveContract?: boolean;
  /** Per-account ACL — false → listed but not openable in this user's scope. */
  canView?: boolean;
}

export interface ClientContract {
  id: string;
  num: string;
  legalEntityName: string;
  type: string;
  status: string;
  validUntil: string | null;
  source?: "crm_managed" | "sync_1c";
}

export interface ClientActivity {
  id: string;
  kind: string;
  title: string;
  who: string;
  when: string;
}

export interface ClientComplaint {
  id: string;
  title: string;
  status: string;
}

export function fetchClientTab<T = Record<string, unknown>>(
  holdingId: string,
  tab: ClientTabKey,
  signal?: AbortSignal,
): Promise<ClientTabResponse<T>> {
  return apiFetch<ClientTabResponse<T>>(
    `/Custom/frontend/clients/${encodeURIComponent(holdingId)}/${tab}`,
    { signal },
  );
}

// ── Этап 2 «Контакти» — holding-master aggregate + write ─────────────────────

export type ContactStatus = "active" | "inactive" | "left_company" | "unverified";

export interface Client360ContactMethod {
  id: string;
  type: string;
  channel: "phone" | "email" | "messenger" | "other";
  value: string;
  label: string;
  isPrimary: boolean;
  source: string;
}

export interface Client360ContactRole {
  code: string;
  label: string;
}

export interface Client360ContactCard {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  position: string;
  roleCodes: string[];
  roles: Client360ContactRole[];
  status: ContactStatus;
  statusLabel: string;
  source: string;
  sourceLabel: string;
  dqFlags: string[];
  incomplete: boolean;
  methods: Client360ContactMethod[];
  hasVisibleMethods: boolean;
  primary: { isScopePrimary: boolean; scopeLabel: string | null; processes: string[] };
  assignmentLabels: string[];
  isFormer: boolean;
  allowedActions: { canEdit: boolean; canChangeStatus: boolean; canConfirm: boolean };
}

export interface Client360ContactsScopeOption { id: string; name: string }

/** §8 type-aware ContactDrawer — classification signals (record_type wins). */
export interface ContactClientType {
  recordType: string;
  partyType: string;
  businessModel: string | null;
}

export interface Client360ContactsResponse {
  items: Client360ContactCard[];
  formerItems: Client360ContactCard[];
  counts: { active: number; former: number; total: number };
  allowedActions: { canCreate: boolean; canMerge: boolean; canRequestReview: boolean };
  requiredRoles?: string[];
  /** absent (restricted path / stale cache) → drawer falls back to lean+expander. */
  clientType?: ContactClientType;
  scopes: {
    holding: Client360ContactsScopeOption;
    legalEntities: Client360ContactsScopeOption[];
    outlets: Client360ContactsScopeOption[];
  };
  restricted: boolean;
  blockStatus: "ok" | "empty" | "forbidden";
}

export function fetchClient360Contacts(
  holdingId: string,
  filters: { role?: string; incomplete?: boolean } = {},
  signal?: AbortSignal,
): Promise<Client360ContactsResponse> {
  return apiFetch<Client360ContactsResponse>(
    `/Custom/frontend/client360/${encodeURIComponent(holdingId)}/contacts`,
    {
      query: {
        role: filters.role || undefined,
        incomplete: filters.incomplete ? "1" : undefined,
      },
      signal,
    },
  );
}

export interface Client360ContactCreatePayload {
  contact: {
    firstName: string;
    lastName: string;
    cCMiddleName?: string;
    cCPosition?: string;
    cCNotes?: string;
  };
  assignment?: {
    scope: "holding" | "legal_entity" | "outlet";
    targetAccountId?: string | null;
    targetOutletId?: string | null;
    roles?: string[];
    isPrimaryForScope?: boolean;
    primaryForOrders?: boolean;
    primaryForPayments?: boolean;
  } | null;
  methods?: { type: string; value: string; isPrimary?: boolean }[];
  acknowledgedDuplicateContactIds?: string[];
  primaryConflictResolution?: string;
}

export interface ContactDuplicateCandidate { id: string; name: string; position?: string }

export interface Client360ContactCreateResponse {
  status: "success" | "duplicate_warning";
  id?: string;
  incomplete?: boolean;
  candidates?: ContactDuplicateCandidate[];
}

export function createClient360Contact(
  holdingId: string,
  payload: Client360ContactCreatePayload,
): Promise<Client360ContactCreateResponse> {
  return apiFetch<Client360ContactCreateResponse>(
    `/Custom/frontend/client360/${encodeURIComponent(holdingId)}/contacts`,
    { method: "POST", body: payload },
  );
}

export interface Client360ContactUpdatePayload {
  contact?: { firstName?: string; lastName?: string; cCMiddleName?: string; cCPosition?: string; cCNotes?: string };
  addMethods?: { type: string; value: string; isPrimary?: boolean }[];
  roles?: string[];
}

export function updateClient360Contact(
  contactId: string,
  payload: Client360ContactUpdatePayload,
): Promise<{ status: string; id: string }> {
  return apiFetch(
    `/Custom/frontend/client360/contacts/${encodeURIComponent(contactId)}/update`,
    { method: "POST", body: payload },
  );
}

export function changeClient360ContactStatus(
  contactId: string,
  status: ContactStatus,
  note?: string,
): Promise<{ status: string; id: string; contactStatus: ContactStatus }> {
  return apiFetch(
    `/Custom/frontend/client360/contacts/${encodeURIComponent(contactId)}/status`,
    { method: "POST", body: { status, note } },
  );
}

export function requestClient360ContactReview(
  contactId: string,
  candidateId: string,
  reason?: string,
): Promise<{ status: string }> {
  return apiFetch(
    `/Custom/frontend/client360/contacts/${encodeURIComponent(contactId)}/request-review`,
    { method: "POST", body: { candidateId, reason } },
  );
}

/** Synchronous CONTACT-specific duplicate pre-check for the add-drawer (AC-7). */
export function contactDuplicateDryRun(
  firstName: string,
  lastName: string,
  methods: { type?: string; value: string }[],
): Promise<{ duplicateFound: boolean; candidates: ContactDuplicateCandidate[]; message: string | null }> {
  return apiFetch(`/Custom/contacts/quick-create/dry-run`, {
    method: "POST",
    body: { contact: { firstName, lastName }, methods },
  });
}

// ── F-1 Legal-entity (Account) detail card ──────────────────────────────────
export interface LegalEntityDetail {
  id: string;
  synced: boolean;
  sourceSystem: string | null;
  identity: {
    name: string;
    legalName: string | null;
    tradeName: string | null;
    legalForm: string | null;
    partyType: string;
    /** §C — legal-identity axis: source of truth for the LE-card type label. */
    legalIdentityType: LegalIdentityType | "unknown";
    active: boolean;
  };
  registry: {
    edrpou: string | null;
    ipn: string | null;
    vatStatus: string;
    vatNumber: string | null;
    taxSystem: string | null;
    legalAddress: string | null;
    actualAddress: string | null;
    code1c: string | null;
    sourceRecordId: string | null;
    syncedAt: string | null;
  };
  finance: {
    // BI-owned (read-only, snapshot at biSnapshotAt).
    arBalance: number | null;
    arOverdue: number | null;
    arOverdueDays: number | null;
    lastPaymentDate: string | null;
    lastOrderDate: string | null;
    // договір-level (representative contract) — NOT BI. Labelled separately in UI.
    creditLimit: number | null;
    deferralDays: number | null;
    currency: string | null;
    termsSource?: string;
  };
  contracts: ClientContract[];
  contacts: LeContact[];
  outlets: LeOutlet[];
  outletsCount: number;
  /** BI snapshot datetime for the finance block. */
  biSnapshotAt?: string | null;
  /** Data-quality flags (type-aware). */
  dq?: string[];
  allowedActions: {
    canRequestChange: boolean;
    changeDisabledReason?: string;
    canBindContact?: boolean;
    canAddOutlet?: boolean;
    outletDisabledReason?: string;
  };
}

export interface LeContact {
  id?: string;
  name: string;
  role: string;
  phone: string;
  primary: boolean;
}

export interface LeOutlet {
  id: string;
  name: string;
  type: string;
  location: string | null;
  active: boolean;
}

export function fetchLegalEntityDetail(
  accountId: string,
  signal?: AbortSignal,
): Promise<LegalEntityDetail> {
  return apiFetch<LegalEntityDetail>(
    `/Custom/frontend/legal-entities/${encodeURIComponent(accountId)}`,
    { signal },
  );
}

// ── Этап 4 — «Структура» tree ────────────────────────────────────────────────

export interface StructureLegalEntity {
  id: string;
  name: string;
  reg: string;
  isPrimary: boolean;
  active: boolean;
  /** Per-LE debt figure ONLY — never a holding total (BI owns holding-AR). */
  debt: number;
  hasActiveContract: boolean;
  outlets: LeOutlet[];
  contacts: LeContact[];
}

export interface StructureResponse {
  holdingId: string;
  legalEntities: StructureLegalEntity[];
}

export function fetchStructure(holdingId: string, signal?: AbortSignal): Promise<StructureResponse> {
  return apiFetch<StructureResponse>(
    `/Custom/frontend/clients/${encodeURIComponent(holdingId)}/structure`,
    { signal },
  );
}
